<style scoped>
.email-list {
	max-height: 500px;
	overflow-y: auto;
}
.email-row {
	padding: 0.5rem 0.75rem;
	border-bottom: 1px solid #e9ecef;
}
.email-row:last-child {
	border-bottom: none;
}
.email-subject {
	font-weight: 500;
}
.email-route {
	font-size: 0.75rem;
	color: #6c757d;
	word-break: break-all;
}
.email-when {
	font-size: 0.75rem;
	white-space: nowrap;
}
.empty-state {
	text-align: center;
	padding: 2rem;
	color: #6c757d;
}
</style>
<template>
	<div class="card mb-3">
		<div class="card-header d-flex justify-content-between align-items-center">
			<span>
				<i class="bi bi-envelope"></i>
				Email History
			</span>
			<span class="badge bg-secondary">
				{{ sortedMessages.length }}
			</span>
		</div>
		<div class="card-body p-0">
			<div v-if="sortedMessages.length === 0" class="empty-state">
				<i class="bi bi-envelope fs-1"></i>
				<p class="mb-0 mt-2">No emails</p>
			</div>
			<div v-else class="email-list">
				<div
					v-for="(msg, idx) in sortedMessages"
					:key="idx"
					class="email-row d-flex justify-content-between align-items-start gap-2"
				>
					<div class="flex-grow-1">
						<div class="d-flex align-items-center gap-2">
							<span
								class="badge"
								:class="
									direction(msg) === 'Sent'
										? 'bg-primary'
										: 'bg-light text-dark border'
								"
							>
								{{ direction(msg) }}
							</span>
							<span
								class="email-subject"
								:class="{ 'text-muted fst-italic': !msg.subject }"
							>
								{{ msg.subject || "(no subject)" }}
							</span>
						</div>
						<div class="email-route">
							{{ msg.source }} → {{ msg.destination }}
						</div>
					</div>
					<div class="email-when text-end text-muted">
						<LocalizedTime :datetime="msg.created" />
						<div v-if="msg.sent_at">
							sent <LocalizedTime :datetime="msg.sent_at" />
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import LocalizedTime from "@/components/LocalizedTime.vue";
import type { ContactEmailMessageDTO } from "@/type/api";

// A contact's email log, newest first. Direction is derived from which side
// carries one of the contact's own addresses: a match on `destination` makes
// the row outbound (Sent), a match on `source` inbound (Received), Sent winning
// when both match, and Sent when neither does — the log is also our own
// outbound record, so it matched on destination by construction. The backend
// caps the history at the 200 newest rows, so this component renders whatever
// it is handed.
interface Props {
	messages?: ContactEmailMessageDTO[];
	contactEmails?: string[];
}

const props = withDefaults(defineProps<Props>(), {
	messages: () => [],
	contactEmails: () => [],
});

const sortedMessages = computed(() => {
	return [...props.messages].sort(
		(a: ContactEmailMessageDTO, b: ContactEmailMessageDTO) =>
			Date.parse(b.created) - Date.parse(a.created),
	);
});

const normalisedEmails = computed(() => {
	return new Set(
		props.contactEmails.map((email: string) => email.trim().toLowerCase()),
	);
});

function direction(msg: ContactEmailMessageDTO): "Sent" | "Received" {
	const emails = normalisedEmails.value;
	if (emails.has(msg.destination.trim().toLowerCase())) {
		return "Sent";
	}
	if (emails.has(msg.source.trim().toLowerCase())) {
		return "Received";
	}
	return "Sent";
}
</script>
